import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { untilDestroyed } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { IBaseSearchRequest, IGridResult } from 'src/app/core/models/base';
import { DeviceService } from 'src/app/devices/services/device.service';
import { ConfirmationDialogService } from 'src/app/shared/services/confirmation-dialog.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import * as DeviceLocationsActions from "../../stores/global/device-locations/device-locations-actions";
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { EditEvent, RemoveEvent } from '@progress/kendo-angular-grid';
import { ICreateDeviceLocationRequest, IDeviceLocation, IUpdateDeviceLocationRequest } from 'src/app/devices/models/device-location';
import { deviceLocationSelector, isLoadingSelector } from 'src/app/stores/global/device-locations/device-location-selectors';
import { DeviceLocationValidator } from 'src/app/shared/validators/device-location-validator';
import { ICompany } from '../models/company';


@Component({
    selector: 'app-device-locations',
    templateUrl: './device-locations.component.html',
    styleUrl: './device-locations.component.scss',
    standalone: false
})
export class DeviceLocationsComponent implements OnInit{
  isLoading$: Observable<boolean>;
  deviceLocations$: Observable<IDeviceLocation[]>;

  @Input() company: ICompany;

  constructor(    
    private route : ActivatedRoute,
    private deviceService: DeviceService,
    private appNotificationService: AppNotificationService,
    private confirmationDialogService : ConfirmationDialogService,
    private store: Store<AppStateInterface>) {
      this.isLoading$ = this.store.pipe(select(isLoadingSelector)),
      this.deviceLocations$ = this.store.pipe(select(deviceLocationSelector))
  }
  
  deviceLocations: IGridResult<IDeviceLocation[]>;
  public formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;
  selectedLocation: IDeviceLocation = null;
  isDialogActive = false;
  public editForm: UntypedFormGroup;
  isInEditing = false;

  public searchRequest : IBaseSearchRequest = {
    pageNumber:0, 
    pageSize:999999
  }

  ngOnInit(): void {
    this.store.dispatch(DeviceLocationsActions.getDeviceLocationsByCompany({companyId: this.company.id}));
  }

  addHandler({ sender }){
    this.closeEditor(sender);

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(null, {
        validators: Validators.required,
        asyncValidators: DeviceLocationValidator(this.deviceService, this.company.id),
        updateOn: 'change'}),
    });

    sender.addRow(this.formGroup);
  }

  public editHandler(args: EditEvent): void{
    this.closeEditor(args.sender);
    this.isInEditing = true;

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(
        args.dataItem.name, {
          validators: Validators.required, 
          asyncValidators: DeviceLocationValidator(this.deviceService, this.company.id),
          updateOn: 'blur' 
        })
    });

    this.editedRowIndex = args.rowIndex;

    args.sender.editRow(args.rowIndex, this.formGroup);
  }

  public saveHandler(event) {
    const createRequest: ICreateDeviceLocationRequest = {
      name: event.formGroup.value.name,
      companyId: this.company.id
    }

    const updateRequest: IUpdateDeviceLocationRequest = {
      name: event.formGroup.value.name,
      id: event.dataItem.id
    }

    if (this.isInEditing) {
      this.store.dispatch(DeviceLocationsActions.updateDeviceLocation({ deviceLocation: updateRequest }));
      this.isInEditing = false;
    } else {
      this.store.dispatch(DeviceLocationsActions.createDeviceLocation({ deviceLocation: createRequest }));
    }
    
    event.sender.closeRow(event.rowIndex);
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
  
  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  public removeHandler(args: RemoveEvent): void{
    var subscription : Subscription = new Subscription();
    this.confirmationDialogService.open();
    subscription = this.confirmationDialogService.decision$.subscribe(val => {
      if (val) {
        // Paste Code from above
        // remove the current dataItem from the current data source,
        // `editService` in this example
        this.store.dispatch(DeviceLocationsActions.deleteDeviceLocation({ id: args.dataItem.id }))
        this.confirmationDialogService.updateValue(false);
      } else{
        subscription.unsubscribe();
      }
    });
  }

  closeDialog(){
    this.selectedLocation = null;
    this.isDialogActive = false;
  }

  onCancel(e: PointerEvent){
    e.preventDefault();
    this.closeDialog();
  }
}
